import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Event } from '@/types/events';
import { EventsService } from '@/services';
import { RootState } from '@/state/store';

interface EventState {
  eventQueue: Event[];
  flushInProgress: boolean;
  autoFlushQueueSize: number;
}

const initialState: EventState = {
  eventQueue: [],
  flushInProgress: false,
  autoFlushQueueSize: 10,
};

// Track a new event (only adds to queue)
export const trackEvent = createAsyncThunk(
  'events/trackEvent',
  async (event: Omit<Event, 'id' | 'timestamp'>) => {
    const enrichedEvent: Event = {
      ...event,
      id: `evt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
    };
    
    return enrichedEvent;
  }
);

// Check if flush is needed and trigger it
export const checkAndFlush = createAsyncThunk(
  'events/checkAndFlush', 
  async (customThreshold?: number, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { eventQueue, autoFlushQueueSize } = state.events;
    
    // Use custom threshold if provided, otherwise use default
    const threshold = customThreshold ?? autoFlushQueueSize;
    
    if (eventQueue.length >= threshold) {
      dispatch(flushEvents());
    }
  }
);

// Flush events to database
export const flushEvents = createAsyncThunk(
  'events/flushEvents',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const { eventQueue } = state.events;
    
    if (eventQueue.length === 0) {
      return { eventCount: 0 };
    }
    
    try {
      await EventsService.createEvents(eventQueue);
      return { eventCount: eventQueue.length };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to flush events');
    }
  }
);



const eventsSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    clearQueue: (state) => {
      state.eventQueue = [];
    },
    resetFlushStatus: (state) => {
      state.flushInProgress = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(trackEvent.fulfilled, (state, action) => {
        state.eventQueue.push(action.payload);
      })
      .addCase(flushEvents.pending, (state) => {
        state.flushInProgress = true;
      })
      .addCase(flushEvents.fulfilled, (state) => {
        state.flushInProgress = false;
        state.eventQueue = [];
      })
      .addCase(flushEvents.rejected, (state) => {
        state.flushInProgress = false;
      });
  },
});

export const { clearQueue, resetFlushStatus } = eventsSlice.actions;

export const selectEventQueue = (state: RootState) => state.events.eventQueue;
export const selectEventQueueSize = (state: RootState) => state.events.eventQueue.length;
export const selectFlushInProgress = (state: RootState) => state.events.flushInProgress;

export default eventsSlice.reducer;
